class ProductInformation extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId
    this.handleEvents();
  }

  connectedCallback() {
    if (this.querySelector(".pinfo-gallery-swiper")) this.initSwiperSlider();
    this.querySelector("pinfo-variant-picker")?.addEventListener(
      "input",
      this.handleVariantEvent.bind(this)
    );
    
    // Functions for mobile
    if (window.innerWidth < 989) {
      this.loaderPdpMobile();
      this.initMobileSwiper();
    }
    if (window.innerWidth >= 990) {
      this.loaderPdp();
      this.initImageZoomOnHover();
    }
    
    this.setupIntersectionObserver();
    this.initQuantitySelector();
    this.colorLabelHover();
    this.optionLabelHover();

    // ============================================================
    // INIT STICKY MOBILE PREVIEW
    // ============================================================
    this.initStickyMobilePreview();
  }

  disconnectedCallback() {
    // Clean up sticky preview
    if (this._stickyCleanup) {
      this._stickyCleanup();
    }
  }

  // ============================================================
  // MOBILE MONOGRAM PREVIEW STICKY BEHAVIOR
  // ============================================================
  initStickyMobilePreview() {
    if (window.innerWidth > 989) return;

    const mainSwiperWrapper = document.querySelector('.pdp-media-sm .swiper');
    const customPlacementWrapper = document.querySelector('.cpc-placement-wrapper.cpc-placement-block-wrapper');
    const body = document.body;

    if (!mainSwiperWrapper || !customPlacementWrapper) {
      return;
    }

    const hasMonogram = !!document.querySelector('.cpc-placement-wrapper');
    if (!hasMonogram) {
      return;
    }

    function isMonogramSlideActive() {
      const monogramSlide = document.querySelector('.swiper-slide.cpc-monogram-slide-wrapper');
      if (!monogramSlide) return false;
      return monogramSlide.classList.contains('swiper-slide-active');
    }

    function isElementInViewport(el) {
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    function doesMonogramSlideExist() {
      return !!document.querySelector('.swiper-slide.cpc-monogram-slide-wrapper');
    }

    function isAtTopOfPage() {
      return window.scrollY <= 50;
    }

    let isSticky = false;
    let scrollTimeout = null;

    function setSticky(shouldSticky) {
      if (shouldSticky === isSticky) return;
      isSticky = shouldSticky;
      if (isSticky) {
        mainSwiperWrapper.classList.add('sticky-preview');
        body.classList.add('sticky-monogram-active');
      } else {
        mainSwiperWrapper.classList.remove('sticky-preview');
        body.classList.remove('sticky-monogram-active');
      }
    }

    function handleScroll() {
      if (window.innerWidth > 989) {
        setSticky(false);
        return;
      }
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = requestAnimationFrame(() => {
        const atTop = isAtTopOfPage();
        const slideExists = doesMonogramSlideExist();
        const slideActive = isMonogramSlideActive();
        const isInViewport = isElementInViewport(customPlacementWrapper);
        const shouldBeSticky = !atTop && slideExists && slideActive && isInViewport;
        setSticky(shouldBeSticky);
      });
    }

    function handleResize() {
      if (window.innerWidth > 989) {
        setSticky(false);
      } else {
        handleScroll();
      }
    }

    function setupSwiperListeners() {
      const mainSwiperEl = document.querySelector('.pdp-media-sm .swiper');
      if (mainSwiperEl && mainSwiperEl.swiper) {
        if (mainSwiperEl._stickySlideChangeHandler) {
          mainSwiperEl.swiper.off('slideChange', mainSwiperEl._stickySlideChangeHandler);
        }
        mainSwiperEl._stickySlideChangeHandler = function() {
          handleScroll();
        };
        mainSwiperEl.swiper.on('slideChange', mainSwiperEl._stickySlideChangeHandler);
        
        mainSwiperEl._stickySlideEndHandler = function() {
          setTimeout(handleScroll, 50);
        };
        mainSwiperEl.swiper.on('slideChangeTransitionEnd', mainSwiperEl._stickySlideEndHandler);
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    setTimeout(setupSwiperListeners, 500);

    const swiperObserver = new MutationObserver(() => {
      setupSwiperListeners();
    });
    const mainSwiperEl = document.querySelector('.pdp-media-sm .swiper');
    if (mainSwiperEl) {
      swiperObserver.observe(mainSwiperEl, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    document.addEventListener('monogram:updated', () => {
      setTimeout(handleScroll, 100);
    });
    setTimeout(handleScroll, 600);

    this._stickyCleanup = function() {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      swiperObserver.disconnect();
      const mainSwiperEl = document.querySelector('.pdp-media-sm .swiper');
      if (mainSwiperEl && mainSwiperEl.swiper) {
        if (mainSwiperEl._stickySlideChangeHandler) {
          mainSwiperEl.swiper.off('slideChange', mainSwiperEl._stickySlideChangeHandler);
          mainSwiperEl._stickySlideChangeHandler = null;
        }
        if (mainSwiperEl._stickySlideEndHandler) {
          mainSwiperEl.swiper.off('slideChangeTransitionEnd', mainSwiperEl._stickySlideEndHandler);
          mainSwiperEl._stickySlideEndHandler = null;
        }
      }
      setSticky(false);
      body.classList.remove('sticky-monogram-active');
    };
  }

  colorLabelHover() {
    const selectedColor = this.querySelector('.pinfo-selected-color');
    if (!selectedColor) return;
    const originalText = selectedColor.textContent;
    this.querySelectorAll('.pinfo-color-label').forEach((label) => {
      label.addEventListener('mouseenter', () => {
        const title = label.getAttribute('title');
        if (title) selectedColor.textContent = title;
      });
      label.addEventListener('mouseleave', () => {
        selectedColor.textContent = originalText;
      });
    });
  }

  optionLabelHover() {
    const selectedOption = this.querySelector('[data-dynamic-size-label]');
    const originalText = selectedOption?.textContent;
    this.querySelectorAll('.pinfo-options-values-wrapper.others-options .pinfo-option-value').forEach((label) => {
      let selectedOption2 = label.closest('.pinfo-variants-options')?.querySelector('span.pinfo-selected-value');
      if (!selectedOption2) return;
      let originalText2 = selectedOption2.textContent; 
      label.addEventListener('mouseenter', () => {
        const title = label.querySelector('label').getAttribute('title');
        if (title) selectedOption2.textContent = title;
      });
      label.addEventListener('mouseleave', () => {
        selectedOption2.textContent = originalText2;
      });
    });
  }

  initQuantitySelector() {
    const qtyWrapper = this.querySelector('[data-quantity-selector]');
    if (!qtyWrapper) return;
    
    if (this._quantityDelegationHandler) {
      qtyWrapper.removeEventListener('click', this._quantityDelegationHandler);
    }
    
    const input = qtyWrapper.querySelector('.qty-input');

    const updateAllQuantityInputs = (value) => {
      const allForms = document.querySelectorAll('form[action$="/cart/add"]');
      allForms.forEach(form => {
        const qtyInput = form.querySelector('input[name="quantity"]');
        if (qtyInput) {
          qtyInput.value = value;
        }
      });
    };

    this._quantityDelegationHandler = (e) => {
      const target = e.target;
      
      if (target.classList.contains('decrease')) {
        let currentVal = parseInt(input.value) || 1;
        if (currentVal > parseInt(input.min || 1)) {
          input.value = currentVal - 1;
          updateAllQuantityInputs(input.value);
          this.dispatchQuantityChange(input.value);
        }
      } else if (target.classList.contains('increase')) {
        let currentVal = parseInt(input.value) || 1;
        input.value = currentVal + 1;
        updateAllQuantityInputs(input.value);
        this.dispatchQuantityChange(input.value);
      }
    };
    
    qtyWrapper.addEventListener('click', this._quantityDelegationHandler);
    
    input.addEventListener('input', () => {
      if (input.value < 1) input.value = 1;
      updateAllQuantityInputs(input.value);
      this.dispatchQuantityChange(input.value);
    });

    updateAllQuantityInputs(input.value);
  }

  dispatchQuantityChange(value) {
    this.dispatchEvent(new CustomEvent('quantity-change', {
      detail: { quantity: Number(value) },
      bubbles: true
    }));
  }

  setupIntersectionObserver() {
    if (this.closest(".has-customizer-monogram")) return;

    const pdpFormBtns = this.querySelector(".pdp-form-btns");
    const targetElement = this.querySelector(".pinfo-form-wrapper");
    let lastScrollY = window.scrollY;

    if (!pdpFormBtns || !targetElement) return;

    window.addEventListener(
      "scroll",
      () => {
        // The class can be added after page load (endpoint fetch), so check on every scroll
        if (this.closest(".has-customizer-monogram")) return;
        const currentScrollY = window.scrollY;
        const direction = currentScrollY > lastScrollY ? "down" : "up";
        const targetRect = targetElement.getBoundingClientRect();

        if (direction === "down" && targetRect.bottom <= 0) {
          pdpFormBtns.classList.remove("in-viewport");
        } else if (
          direction === "up" &&
          targetRect.top < window.innerHeight &&
          targetRect.bottom > 0
        ) {
          pdpFormBtns.classList.add("in-viewport");
        }

        lastScrollY = currentScrollY;
      },
      { passive: true }
    );
  }

  loaderPdp() {
    this.querySelectorAll(".color__variant__btn").forEach((cvBtn) => {
      cvBtn.addEventListener("click", () => {
        document.querySelectorAll(".info-media-image").forEach((e) => {
          e.classList.add("pdp__image_loader");
          setTimeout(() => {
            e.classList.remove("pdp__image_loader");
          }, 2000);
        });
      });
    });
  }

  loaderPdpMobile() {
    this.querySelectorAll(".color__variant__btn").forEach((cvBtn) => {
      cvBtn.addEventListener("click", () => {
        document.querySelectorAll(".pdp_media__wrap_sm").forEach((e) => {
          e.classList.add("pdp__image_loader");
          setTimeout(() => {
            e.classList.remove("pdp__image_loader");
          }, 2000);
        });
      });
    });
  }

  initMobileSwiper() {
    this.mobielSwiper = new Swiper(".pdp-media-sm .swiper", {
      slidesPerView: 1,
      spaceBetween: 16,
      scrollbar: {
        el: ".swiper-scrollbar",
        draggable: true,
      },
      pagination: {
        el: ".pdp-media-sm .swiper-pagination",
      },
      on: {
        init: function () {
          document.querySelector(".total").textContent = this.slides.length;
          document.querySelector(".current").textContent = this.realIndex + 1;
        },
        slideChange: function () {
          document.querySelector(".current").textContent = this.realIndex + 1;
        },
      },
    });
  }

  initColorOptionsSwiper() {
    this.colorOptionsSwiper = new Swiper(".color-options .swiper", {
      slidesPerView: "auto",
      spaceBetween: 10,
      navigation: {
        nextEl: ".color-options .swiper-button-next",
        prevEl: ".color-options .swiper-button-prev",
      },
      on: {
        init: function () {
          const colorOptions = this.el.closest(".color-options");
          const nextButton = colorOptions.querySelector(".swiper-button-next");
          const prevButton = colorOptions.querySelector(".swiper-button-prev");

          const checkLockClasses = () => {
            if (
              nextButton.classList.contains("swiper-button-lock") &&
              prevButton.classList.contains("swiper-button-lock")
            ) {
              colorOptions.classList.add("no-padding");
            } else {
              colorOptions.classList.remove("no-padding");
            }
          };

          checkLockClasses();

          const observer = new MutationObserver(checkLockClasses);
          observer.observe(nextButton, {
            attributes: true,
            attributeFilter: ["class"],
          });
          observer.observe(prevButton, {
            attributes: true,
            attributeFilter: ["class"],
          });
        },
      },
    });
  }

  initImageZoomOnHover() {
    if (window.innerWidth <= 989) return;

    const imageContainers = this.querySelectorAll(".pdp-media-lg .pdp__image");

    imageContainers.forEach((container) => {
      const img = container.querySelector("img.media");
      
      if (!img) return;

      img.style.transition = "transform 0.3s ease";

      let isZoomed = false;

      container.addEventListener("click", () => {
        isZoomed = !isZoomed;

        if (isZoomed) {
          container.classList.add("zoomed");
        } else {
          container.classList.remove("zoomed");
          img.style.transformOrigin = "center center";
        }
      });

      container.addEventListener("mousemove", (e) => {
        if (!isZoomed) return;

        const rect = img.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        img.style.transformOrigin = `${x}% ${y}%`;
      });

      container.addEventListener("mouseleave", () => {
        isZoomed = false;
        container.classList.remove("zoomed");
        img.style.transformOrigin = "center center";
      });
    });
  }

  handleEvents() {
    this.form = this.querySelector('.pdp-form-btns form[action$="/cart/add"]');
    this.cartDrawer = document.querySelector("store-cart-drawer");

    if (!this.form) return;

    if (this._boundHandleAddToCart) {
      this.form.removeEventListener('submit', this._boundHandleAddToCart);
    }

    this._boundHandleAddToCart = this.handleAddToCart.bind(this);
    this.form.addEventListener('submit', this._boundHandleAddToCart);
  }

  handleVariantEvent(e) {
    const { variantUrl } = e.target.dataset;
    const isColorInput = e.target.classList.contains('pinfo-color-input');
    this.fetchData(variantUrl, isColorInput);
    this.updateURL(variantUrl);
    const variantId = Number(
      new URLSearchParams(variantUrl.split("?")[1]).get("variant")
    );

    if (document.querySelector("stock-notify")) {
      // notifyPopup(selectedVariant);
    }
  }

  async fetchData(url, isColorInput = false) {
    if (this.querySelector("pinfo-variant-picker")) {
      this.querySelector("pinfo-variant-picker").style.pointerEvents = "none";
      this.querySelector("pinfo-variant-picker").style.opacity = "0.7";
    }

    let sectionUrl = `${url}&section_id=${this.sectionId}`

    if (location.href.includes('/pages/')) {
      sectionUrl = `${url}`
    }
    
    const response = await fetch(sectionUrl);
    const data = await response.text();
    const html = new DOMParser().parseFromString(data, "text/html");

    this.renderEl(html, isColorInput);
    window.sizeGuide();
    window.setupHeaderDrawer();
  }


  /*
  renderEl(html, isColorInput = false) {
    // ── Detect whether a customizer or placement is on the page ──
    const hasCustomizer = !!document.querySelector('.customizer-groups-container');
    const hasPlacement = !!document.querySelector('.cpc-placement-wrapper');

    const variantPickers = this.querySelectorAll("pinfo-variant-picker");
    const htmlVariantPickers = html.querySelectorAll("pinfo-variant-picker");
    variantPickers.forEach((picker, index) => {
      if (htmlVariantPickers[index]) {
        picker.innerHTML = htmlVariantPickers[index].innerHTML;
      }
    });

    // ── PQA: re-render then immediately re-sync customizer properties ──
    if (document.querySelector("pqa-new")) {
      document.querySelector("pqa-new").innerHTML =
        html.querySelector("pqa-new").innerHTML;

      const sourceContainer = document.querySelector('form[action$="/cart/add"] .customizer-properties');
      const pqaForm = document.querySelector('pqa-new form[action$="/cart/add"]');
      if (sourceContainer && pqaForm) {
        let mirror = pqaForm.querySelector('.customizer-properties');
        if (!mirror) {
          mirror = document.createElement('div');
          mirror.className = 'customizer-properties';
          mirror.style.display = 'none';
          pqaForm.appendChild(mirror);
        }
        mirror.innerHTML = sourceContainer.innerHTML;
      }
    }

    const prices = this.querySelectorAll("product-information .pinfo-price-wrapper");
    const htmlprices = html.querySelectorAll("product-information .pinfo-price-wrapper");
    prices.forEach((price, index) => {
      if (htmlprices[index]) {
        price.innerHTML = htmlprices[index].innerHTML;
      }
    });

    const saleBadges = this.querySelectorAll(".--pdp__info_sale_badge-wrapper");
    const htmlSaleBadges = html.querySelectorAll(".--pdp__info_sale_badge-wrapper");
    saleBadges.forEach((saleBadge, index) => {
      if (htmlSaleBadges[index]) {
        saleBadge.innerHTML = htmlSaleBadges[index].innerHTML;
      }
    });

    if (this.querySelector(".--inventory-message")) {
      this.querySelector(".--inventory-message").innerHTML =
        html.querySelector(".--inventory-message").innerHTML;
    }

    // ── Form: always full re-render ──
    if (this.querySelector(".pinfo-form-wrapper") && html.querySelector(".pinfo-form-wrapper")) {
      const masterContainer = document.querySelector(
        'form[action$="/cart/add"].shopify-product-form .customizer-properties'
      );
      const savedPropsHTML = masterContainer ? masterContainer.innerHTML : null;

      this.querySelector(".pinfo-form-wrapper").innerHTML =
        html.querySelector(".pinfo-form-wrapper").innerHTML;

      if (savedPropsHTML) {
        const form = this.querySelector('.pinfo-form-wrapper form[action$="/cart/add"]');
        if (form) {
          let propsContainer = form.querySelector('.customizer-properties');
          if (!propsContainer) {
            propsContainer = document.createElement('div');
            propsContainer.className = 'customizer-properties';
            propsContainer.style.display = 'none';
            form.appendChild(propsContainer);
          }
          propsContainer.innerHTML = savedPropsHTML;
        }
      } else if (hasCustomizer) {
        const newForm = this.querySelector('.pinfo-form-wrapper form[action$="/cart/add"]');
        if (newForm) {
          let propsContainer = newForm.querySelector('.customizer-properties');
          if (!propsContainer) {
            propsContainer = document.createElement('div');
            propsContainer.className = 'customizer-properties';
            propsContainer.style.display = 'none';
            newForm.appendChild(propsContainer);
          }
          if (typeof window.__customizerWriteProperties === 'function') {
            window.__customizerWriteProperties();
          }
        }
      }

      if (hasCustomizer) {
        document.querySelectorAll('.pinfo-add-to-cart').forEach(btn => {
          btn.disabled = true;
          btn.setAttribute('disabled', 'disabled');
        });
        document.querySelectorAll('.p-info-btn-buy-now').forEach(btn => {
          btn.disabled = true;
          btn.setAttribute('disabled', 'disabled');
        });

        if (typeof window.__customizerRecheck === 'function') {
          window.__customizerRecheck();
        }
      }


      // ── ADD: Recheck Custom Placement after form update ──
      if (hasPlacement && window.CustomPlacement && window.CustomPlacement.instance) {
        setTimeout(function() {
          window.CustomPlacement.instance.reattachEventListeners();
          window.CustomPlacement.instance.recheckState();
        }, 200);
      }
    }

    if (this.querySelector(".p-model-info") && html.querySelector(".p-model-info")) {
      this.querySelector(".p-model-info").innerHTML =
        html.querySelector(".p-model-info").innerHTML;
    }

    if (this.querySelector(".stock-alert") && html.querySelector(".stock-alert")) {
      this.querySelector(".stock-alert").innerHTML =
        html.querySelector(".stock-alert").innerHTML;
    }

    // ── Media: only update on color change OR if no customizer ──
    if (isColorInput || !hasCustomizer) {
      if (this.querySelector("product-info-media") && html.querySelector("product-info-media")) {
        this.querySelector("product-info-media").innerHTML =
          html.querySelector("product-info-media").innerHTML;
      }
    }

    if (this.querySelector(".--discount-code-container") && html.querySelector(".--discount-code-container")) {
      this.querySelector(".--discount-code-container").innerHTML =
        html.querySelector(".--discount-code-container").innerHTML;
    }

    this.colorLabelHover();
    this.optionLabelHover();
    
    this.handleEvents();
    window.viewerCountPastDay();
    window.notifyButtons();

    if (this.querySelector("pinfo-variant-picker")) {
      this.querySelector("pinfo-variant-picker").style.pointerEvents = "unset";
      this.querySelector("pinfo-variant-picker").style.opacity = "1";
    }

    window.setupHeaderDrawer();
    this.setupIntersectionObserver();

    if (window.innerWidth >= 990) {
      this.initImageZoomOnHover();
      this.loaderPdp();
    } else {
      this.loaderPdpMobile();
      if (isColorInput || !hasCustomizer) {
        this.initMobileSwiper();
      }
    }

    window.mblLightBoxFunc();
    yotpoWidgetsContainer.initWidgets();
    window.copyCode();
    window.globalAtcRemoveFunc();

    if (typeof window.setupSectionNavigatorScroll === "function") {
      window.setupSectionNavigatorScroll();
    }

    // ── Customizer: only re-run on color change ──
    if (isColorInput && typeof window.appCustomizer === "function") {
      window.appCustomizer();
    }

    if (typeof this.initQuantitySelector === 'function') {
      this.initQuantitySelector();
    }

    // Dispatch variant:change event
    const variantId = this.querySelector('input[name="id"]')?.value;
    if (variantId) {
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId } }));
    }
  }
  */

  renderEl(html, isColorInput = false) {
    // Helper function to safely call app customizer recheck
    function recheckAppCustomizer() {
      var recheckFn = window.__appCustomizerRecheck || window.__customizerRecheck;
      if (typeof recheckFn === 'function') {
        setTimeout(function() {
          recheckFn();
        }, 100);
      }
    }

    // ── Detect whether a customizer or placement is on the page ──
    const hasCustomizer = !!document.querySelector('.customizer-groups-container');
    const hasPlacement = !!document.querySelector('.cpc-placement-wrapper');
    const hasAnyCustomizer = hasCustomizer || hasPlacement;

    const variantPickers = this.querySelectorAll("pinfo-variant-picker");
    const htmlVariantPickers = html.querySelectorAll("pinfo-variant-picker");
    variantPickers.forEach((picker, index) => {
      if (htmlVariantPickers[index]) {
        picker.innerHTML = htmlVariantPickers[index].innerHTML;
      }
    });

    // ── PQA: re-render then immediately re-sync customizer properties ──
    if (document.querySelector("pqa-new")) {
      document.querySelector("pqa-new").innerHTML =
        html.querySelector("pqa-new").innerHTML;

      const sourceContainer = document.querySelector('form[action$="/cart/add"] .customizer-properties');
      const pqaForm = document.querySelector('pqa-new form[action$="/cart/add"]');
      if (sourceContainer && pqaForm) {
        let mirror = pqaForm.querySelector('.customizer-properties');
        if (!mirror) {
          mirror = document.createElement('div');
          mirror.className = 'customizer-properties';
          mirror.style.display = 'none';
          pqaForm.appendChild(mirror);
        }
        mirror.innerHTML = sourceContainer.innerHTML;
      }
    }

    const prices = this.querySelectorAll("product-information .pinfo-price-wrapper");
    const htmlprices = html.querySelectorAll("product-information .pinfo-price-wrapper");
    prices.forEach((price, index) => {
      if (htmlprices[index]) {
        price.innerHTML = htmlprices[index].innerHTML;
      }
    });

    const saleBadges = this.querySelectorAll(".--pdp__info_sale_badge-wrapper");
    const htmlSaleBadges = html.querySelectorAll(".--pdp__info_sale_badge-wrapper");
    saleBadges.forEach((saleBadge, index) => {
      if (htmlSaleBadges[index]) {
        saleBadge.innerHTML = htmlSaleBadges[index].innerHTML;
      }
    });

    if (this.querySelector(".--inventory-message")) {
      this.querySelector(".--inventory-message").innerHTML =
        html.querySelector(".--inventory-message").innerHTML;
    }

    // ── Form: always full re-render ──
    if (this.querySelector(".pinfo-form-wrapper") && html.querySelector(".pinfo-form-wrapper")) {
      const masterContainer = document.querySelector(
        'form[action$="/cart/add"].shopify-product-form .customizer-properties'
      );
      const savedPropsHTML = masterContainer ? masterContainer.innerHTML : null;

      this.querySelector(".pinfo-form-wrapper").innerHTML =
        html.querySelector(".pinfo-form-wrapper").innerHTML;

      if (savedPropsHTML) {
        const form = this.querySelector('.pinfo-form-wrapper form[action$="/cart/add"]');
        if (form) {
          let propsContainer = form.querySelector('.customizer-properties');
          if (!propsContainer) {
            propsContainer = document.createElement('div');
            propsContainer.className = 'customizer-properties';
            propsContainer.style.display = 'none';
            form.appendChild(propsContainer);
          }
          propsContainer.innerHTML = savedPropsHTML;
        }
      } else if (hasCustomizer) {
        const newForm = this.querySelector('.pinfo-form-wrapper form[action$="/cart/add"]');
        if (newForm) {
          let propsContainer = newForm.querySelector('.customizer-properties');
          if (!propsContainer) {
            propsContainer = document.createElement('div');
            propsContainer.className = 'customizer-properties';
            propsContainer.style.display = 'none';
            newForm.appendChild(propsContainer);
          }
          if (typeof window.__appCustomizerWriteProperties === 'function') {
            window.__appCustomizerWriteProperties();
          }
        }
      }

      if (hasCustomizer) {
        document.querySelectorAll('.pinfo-add-to-cart').forEach(btn => {
          btn.disabled = true;
          btn.setAttribute('disabled', 'disabled');
        });
        document.querySelectorAll('.p-info-btn-buy-now').forEach(btn => {
          btn.disabled = true;
          btn.setAttribute('disabled', 'disabled');
        });

        // Recheck App Customizer
        recheckAppCustomizer();
        /*
        if (hasCustomizer && typeof window.__appCustomizerRecheck === 'function') {
          setTimeout(function() {
            window.__appCustomizerRecheck();
          }, 100);
        }
        */
      }

      // ── Recheck Custom Placement after form update ──
      if (hasPlacement && window.CustomPlacement && window.CustomPlacement.instance) {
        setTimeout(function() {
          window.CustomPlacement.instance.reattachEventListeners();
          window.CustomPlacement.instance.recheckState();
        }, 200);
      }

      // ============================================================
      // FOR NON-COLOR CHANGES (Size, etc.) - Just recheck, don't re-render media
      // ============================================================
      if (!isColorInput) {
        // Recheck App Customizer
        recheckAppCustomizer();
        /*
        if (hasCustomizer && typeof window.__appCustomizerRecheck === 'function') {
          setTimeout(function() {
            window.__appCustomizerRecheck();
          }, 100);
        }
        */

        // Recheck Custom Placement
        if (hasPlacement && typeof window.__placementRecheck === 'function') {
          setTimeout(function() {
            window.__placementRecheck();
          }, 100);
        }

        // ── IMPORTANT: Skip ONLY media re-render for non-color changes ──
        // BUT continue with everything else below
        // We'll use a flag instead of returning
        var skipMediaRender = true;
      }
    }

    // ── Media: only update on color change OR if no customizer ──
    // Use the skip flag to prevent media re-render on non-color changes
    if ((isColorInput || !hasAnyCustomizer) && typeof skipMediaRender === 'undefined') {
      if (this.querySelector("product-info-media") && html.querySelector("product-info-media")) {
        this.querySelector("product-info-media").innerHTML =
          html.querySelector("product-info-media").innerHTML;
      }
    } else if (isColorInput || !hasAnyCustomizer) {
      // If it's a color change or no customizer, re-render media
      if (this.querySelector("product-info-media") && html.querySelector("product-info-media")) {
        this.querySelector("product-info-media").innerHTML =
          html.querySelector("product-info-media").innerHTML;
      }
    }

    if (this.querySelector(".p-model-info") && html.querySelector(".p-model-info")) {
      this.querySelector(".p-model-info").innerHTML =
        html.querySelector(".p-model-info").innerHTML;
    }

    if (this.querySelector(".stock-alert") && html.querySelector(".stock-alert")) {
      this.querySelector(".stock-alert").innerHTML =
        html.querySelector(".stock-alert").innerHTML;
    }

    if (this.querySelector(".--discount-code-container") && html.querySelector(".--discount-code-container")) {
      this.querySelector(".--discount-code-container").innerHTML =
        html.querySelector(".--discount-code-container").innerHTML;
    }

    this.colorLabelHover();
    this.optionLabelHover();
    
    this.handleEvents();
    window.viewerCountPastDay();
    window.notifyButtons();

    if (this.querySelector("pinfo-variant-picker")) {
      this.querySelector("pinfo-variant-picker").style.pointerEvents = "unset";
      this.querySelector("pinfo-variant-picker").style.opacity = "1";
    }

    window.setupHeaderDrawer();
    this.setupIntersectionObserver();

    if (window.innerWidth >= 990) {
      this.initImageZoomOnHover();
      this.loaderPdp();
    } else {
      this.loaderPdpMobile();
      // Only reinit mobile swiper if media was actually re-rendered
      if (isColorInput || !hasAnyCustomizer) {
        this.initMobileSwiper();
      }
    }

    window.mblLightBoxFunc();
    yotpoWidgetsContainer.initWidgets();
    window.copyCode();
    window.globalAtcRemoveFunc();

    if (typeof window.setupSectionNavigatorScroll === "function") {
      window.setupSectionNavigatorScroll();
    }

    // ── Customizer: only re-run on color change ──
    if (isColorInput && typeof window.appCustomizer === "function") {
      window.appCustomizer();
    }
    // Recheck App Customizer
    recheckAppCustomizer();
    /*
    if (hasCustomizer && typeof window.__appCustomizerRecheck === 'function') {
      setTimeout(function() {
        window.__appCustomizerRecheck();
      }, 100);
    }
    */

    // Recheck Custom Placement
    if (hasPlacement && typeof window.__placementRecheck === 'function') {
      setTimeout(function() {
        window.__placementRecheck();
      }, 100);
    }

    // Also reattach Custom Placement listeners and recheck state
    if (hasPlacement && window.CustomPlacement && window.CustomPlacement.instance) {
      window.CustomPlacement.instance.reattachEventListeners();
      window.CustomPlacement.instance.recheckState();
    }

    if (typeof this.initQuantitySelector === 'function') {
      this.initQuantitySelector();
    }

    // Dispatch variant:change event
    const variantId = this.querySelector('input[name="id"]')?.value;
    if (variantId) {
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId } }));
    }
  }

  updateURL(url) {
    if (typeof url !== "string" || !url.startsWith("/")) {
      console.error("Invalid URL format");
      return;
    }

    if (this.dataset?.updateUrl === "true") {
      window.history.replaceState({}, "", url);
    }
  }

  handleAddToCart(e) {
    e.preventDefault();
    
    let btn = this.querySelector('.pinfo-add-to-cart');
    const originalText = btn ? btn.textContent : null;

    if (btn) {
      btn.classList.add("loading");
      btn.innerHTML = '<span class="loader"></span> Adding...';
      btn.disabled = true;
    }

    const formData = new FormData(this.form);
    const variantId = formData.get("id");

    if (this.cartDrawer && this.cartDrawer.getSectionsToRender) {
      formData.append(
        "sections",
        this.cartDrawer.getSectionsToRender().map((section) => section.id)
      );
    }
    formData.append("sections_url", window.location.pathname);

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (typeof publish === "function") {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: variantId,
              errors: response.errors || response.description,
            });
          }
          throw new Error(response.errors || response.description);
        }

        if (this.cartDrawer && this.cartDrawer.renderContents) {
          this.cartDrawer.renderContents(response);
        }

        if (this.cartDrawer && this.cartDrawer.open) {
          this.cartDrawer.classList.remove("is-empty");
          this.cartDrawer.open();
        }
        if (productQuickView && productQuickView.container && productQuickView.container.classList.contains("open")) {
          productQuickView.container.close();
        }

        if (typeof publish === "function") {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: variantId,
          });
        }
      })
      .catch((error) => {
        console.error("Add to cart failed:", error);
      })
      .finally(() => {
        if (btn) {
          btn.classList.remove("loading");
          btn.innerHTML = originalText;
          btn.disabled = false;
        }
      });
  }

  initSwiperSlider() {
    if (this.gallerySwiper) this.gallerySwiper.destroy(true, true);
    this.gallerySwiper = new Swiper(".pinfo-gallery-swiper", {
      slidesPerView: 2,
      spaceBetween: 0,
      watchSlidesProgress: true,
    });

    if (this.thumbSwiper) this.thumbSwiper.destroy(true, true);
    this.thumbSwiper = new Swiper(".pinfo-thumb-swiper", {
      slidesPerView: 1,
      thumbs: {
        swiper: this.gallerySwiper,
      },
    });
  }
}

document.addEventListener("DOMContentLoaded", function () {
  const body = document.body;
  const scrollThreshold = 300;
  let isScrolled = false;

  window.addEventListener("scroll", function () {
    const shouldBeScrolled = window.scrollY > scrollThreshold;

    if (shouldBeScrolled !== isScrolled) {
      isScrolled = shouldBeScrolled;
      if (isScrolled) {
        body.classList.add("pad__bottom");
      } else {
        body.classList.remove("pad__bottom");
      }
    }
  });
});

window.initFaqAccordions = function () {
  const faqAccordions = document.querySelectorAll(".pr-faq-item");
  const openFaqAccordion = (accordion) => {
    const content = accordion.querySelector(".pr-faq-content");
    accordion.classList.add("active");
    content.style.maxHeight = content.scrollHeight + "px";
  };

  const closeFaqAccordion = (accordion) => {
    const content = accordion.querySelector(".pr-faq-content");
    accordion.classList.remove("active");
    content.style.maxHeight = null;
  };

  if (faqAccordions.length > 0) {
    openFaqAccordion(faqAccordions[0]);

    faqAccordions.forEach((accordion) => {
      const header = accordion.querySelector(".pr-faq-header");
      const content = accordion.querySelector(".pr-faq-content");

      accordion.onclick = () => {
        if (content.style.maxHeight) {
          closeFaqAccordion(accordion);
        } else {
          faqAccordions.forEach((accordion) => closeFaqAccordion(accordion));
          openFaqAccordion(accordion);
        }
      };
    });
  }
};

document.addEventListener("DOMContentLoaded", function () {
  window.initFaqAccordions();
});

class ProductQuickAdd extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("input", this.handleVariantInput.bind(this));
    this.addEventListener("click", this.handleSubmitEvemt.bind(this));
    this.addEventListener("change", this.handleVariantInput.bind(this));
  }

  handleVariantInput(e) {
    let variantUrl = '';
    if (e.target.tagName.toLowerCase() === 'select') {
      const selectedOption = e.target.selectedOptions[0];
      variantUrl = selectedOption ? selectedOption.dataset.variantUrl : null;
    }
    if (variantUrl && document.querySelector("product-information")) {
      const productInfo = document.querySelector("product-information");
      productInfo.fetchData(variantUrl);
      productInfo.updateURL(variantUrl);
    }
  }

  handleSubmitEvemt(e) {
    if (e.target.closest("form")) {
      e.preventDefault();
    }

    if (e.target.tagName === "BUTTON") {
      this.handleAddToCart(e.target.closest("form"), e.target);
    }
  }

  handleAddToCart(form, btn) {
    if (!form) return;

    if (btn) {
      btn.textContent = "Adding To Cart ...";
      btn.style.pointerEvents = "none";
      btn.style.opacity = ".7";
    }
    this.cart = document.querySelector("cart-notification") || document.querySelector("store-cart-drawer");
    let formData = new FormData(form);

    const productInfo = document.querySelector("product-information");

    if (this.cart) {
      formData.append(
        "sections",
        this.cart.getSectionsToRender().map((section) => section.id)
      );
    }
    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        this.cart.renderContents(response);
      })
      .finally(() => {
        if (btn) {
          btn.textContent = "Add To Cart";
          btn.style.pointerEvents = "unset";
          btn.style.opacity = "1";
        }
        if (document.querySelector(".new-header-cart-icon"))
          document.querySelector(".new-header-cart-icon").click();

        if (this.cart && this.cart.classList.contains("is-empty"))
          this.cart.classList.remove("is-empty");
        initswiperincart();
      });
  }
}

document.addEventListener("DOMContentLoaded", (e) => {
  customElements.define("product-information", ProductInformation);
  customElements.define("pqa-new", ProductQuickAdd);
});

window.viewerCountPastDay = function () {
  const viewerElement = document.getElementById("viewer-count");

  if (
    !viewerElement ||
    !viewerElement.dataset.productId ||
    !viewerElement.dataset.min ||
    !viewerElement.dataset.max
  ) {
    return;
  }

  const productId = viewerElement.dataset.productId;
  const storageKey = `viewerCount_${productId}`;
  const min = parseInt(viewerElement.dataset.min);
  const max = parseInt(viewerElement.dataset.max);

  function updateViewerCount() {
    const viewerCount = Math.floor(Math.random() * (max - min + 1)) + min;
    const numberElement = document.getElementById("viewer-number");
    if (numberElement) {
      numberElement.textContent = viewerCount;
      localStorage.setItem(storageKey, viewerCount);
      localStorage.setItem(`${storageKey}_timestamp`, new Date().getTime());
    }
  }

  function isSameDayInEST(timestamp) {
    const now = new Date();
    const estOffset = -5 * 60;
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const estNow = new Date(utc + estOffset * 60000);
    const storedDate = new Date(parseInt(timestamp));
    const estStored = new Date(storedDate.getTime() + estOffset * 60000);

    return (
      estNow.getFullYear() === estStored.getFullYear() &&
      estNow.getMonth() === estStored.getMonth() &&
      estNow.getDate() === estStored.getDate()
    );
  }

  function scheduleDailyUpdate() {
    const storedCount = localStorage.getItem(storageKey);
    const storedTimestamp = localStorage.getItem(`${storageKey}_timestamp`);
    const numberElement = document.getElementById("viewer-number");

    const parsedCount = parseInt(storedCount);
    const isValidCount = !isNaN(parsedCount) && parsedCount >= min && parsedCount <= max;

    if (storedCount && storedTimestamp && isSameDayInEST(storedTimestamp) && isValidCount && numberElement) {
      numberElement.textContent = parsedCount;
    } else {
      updateViewerCount();
    }

    const now = new Date();
    const estOffset = -5 * 60;
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const estNow = new Date(utc + estOffset * 60000);
    const target = new Date(estNow);
    target.setHours(24, 0, 0, 0);

    if (estNow > target) {
      target.setDate(target.getDate() + 1);
    }

    const msUntilMidnight = target.getTime() - estNow.getTime();

    setTimeout(() => {
      updateViewerCount();
      setInterval(updateViewerCount, 24 * 60 * 60 * 1000);
    }, msUntilMidnight);
  }

  scheduleDailyUpdate();
};

window.globalAtcRemoveFunc = function () {
  document.querySelectorAll('.custom__atc-global-btn').forEach(btn => {
    btn.classList.remove('custom__atc-global-btn');
  });
};

document.addEventListener('DOMContentLoaded', function () {
  window.globalAtcRemoveFunc();
});

window.mblLightBoxFunc = function () {
  if (window.innerWidth > 989) return;
  let popupSwiper = null;
  let zoomistInstances = [];

  function createZoomistPopup(activeSlideIndex = 0) {
    const popup = document.getElementById('zoomist-popup');
    const swiperWrapper = popup.querySelector('.swiper-wrapper');

    swiperWrapper.innerHTML = '';
    if (popupSwiper) {
      popupSwiper.destroy(true, true);
      popupSwiper = null;
    }
    zoomistInstances.forEach(({ instance }) => instance.destroy());
    zoomistInstances = [];

    const slides = document.querySelectorAll('.pdp-media-image');
    const imagePromises = [];
    const images = [];
    slides.forEach((slide) => {
      const imgElement = slide.querySelector('img');
      if (!imgElement) return;

      const img = new Image();
      img.src = imgElement.src;
      images.push({ src: imgElement.src, alt: imgElement.alt || '' });
      imagePromises.push(
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = resolve;
          }
        })
      );
    });

    const paginationEl = document.querySelector('.zoomist-popup .swiper-pagination');
    const prevEl = document.querySelector('.zoomist-popup .swiper-button-prev');
    const nextEl = document.querySelector('.zoomist-popup .swiper-button-next');
    const counterEl = document.querySelector('.zoomist-popup .swiper-counter');

    popupSwiper = new Swiper('.zoomist-popup .swiper', {
      slidesPerView: 1,
      spaceBetween: 10,
      initialSlide: activeSlideIndex,
      pagination: { el: paginationEl, clickable: true },
      navigation: { prevEl, nextEl },
      allowTouchMove: true,
      touchRatio: 1,
    });

    Promise.all(imagePromises).then(() => {
      images.forEach((image) => {
        const zoomSlide = document.createElement('div');
        zoomSlide.classList.add('swiper-slide');
        zoomSlide.innerHTML = `
          <figure>
            <div class="zoomist-container" data-zoomist-src="${image.src}">
              <div class="zoomist-wrapper">
                <div class="zoomist-image square">
                  <img src="${image.src}" alt="${image.alt}" decoding="async" />
                </div>
              </div>
            </div>
          </figure>
        `;
        swiperWrapper.appendChild(zoomSlide);

        const container = zoomSlide.querySelector('.zoomist-container');
        let currentScale = 1;
        const zoomistInstance = new Zoomist(container, {
          maxScale: 6,
          minScale: 1,
          bounds: true,
          smooth: true,
          draggable: false,
          wheelable: true,
          pinchable: true,
          on: {
            ready() {
              console.log('Zoomist initialized');
            },
            zoom(zoomist, scale) {
              currentScale = scale;
              zoomist.options.draggable = scale > 1;
              popupSwiper.allowTouchMove = scale === 1;
            },
          },
        });
        zoomistInstances.push({ instance: zoomistInstance, currentScale });
      });

      popupSwiper.update();
      updateCounter();

      setTimeout(() => {
        let lastActiveIndex = activeSlideIndex;
        popup.querySelectorAll('.zoomist-container').forEach((el, index) => {
          let lastTap = 0;

          el.addEventListener('touchstart', (e) => e.stopPropagation());
          el.addEventListener('touchend', (e) => {
            if (e.changedTouches.length !== 1) return;
            e.preventDefault();
            const currentTime = Date.now();
            const tapLength = currentTime - lastTap;
            if (tapLength < 300 && tapLength > 0) {
              const rect = el.querySelector('.zoomist-image').getBoundingClientRect();
              const offsetX = e.changedTouches[0].clientX - rect.left;
              const offsetY = e.changedTouches[0].clientY - rect.top;
              const newScale = zoomistInstances[index].currentScale === 1 ? 3 : 1;
              zoomistInstances[index].instance.zoomTo(newScale, { x: offsetX, y: offsetY });
              zoomistInstances[index].currentScale = newScale;
              zoomistInstances[index].instance.options.draggable = newScale > 1;
              popupSwiper.allowTouchMove = newScale === 1;
            }
            lastTap = currentTime;
          });

          el.addEventListener('click', (e) => {
            const currentTime = Date.now();
            const tapLength = currentTime - lastTap;
            if (tapLength < 300 && tapLength > 0) {
              const rect = el.querySelector('.zoomist-image').getBoundingClientRect();
              const offsetX = e.clientX - rect.left;
              const offsetY = e.clientY - rect.top;
              const newScale = zoomistInstances[index].currentScale === 1 ? 3 : 1;
              zoomistInstances[index].instance.zoomTo(newScale, { x: offsetX, y: offsetY });
              zoomistInstances[index].currentScale = newScale;
              zoomistInstances[index].instance.options.draggable = newScale > 1;
              popupSwiper.allowTouchMove = newScale === 1;
            }
            lastTap = currentTime;
          });
        });

        popupSwiper.on('slideChange', () => {
          const activeIndex = popupSwiper.activeIndex;
          if (lastActiveIndex !== activeIndex && zoomistInstances[lastActiveIndex]) {
            zoomistInstances[lastActiveIndex].instance.reset();
            zoomistInstances[lastActiveIndex].currentScale = 1;
            zoomistInstances[lastActiveIndex].instance.options.draggable = false;
          }
          if (zoomistInstances[activeIndex]) {
            zoomistInstances[activeIndex].instance.update();
            popupSwiper.allowTouchMove = zoomistInstances[activeIndex].currentScale === 1;
          }
          lastActiveIndex = activeIndex;
          updateCounter();
        });

        if (zoomistInstances[activeSlideIndex]) {
          zoomistInstances[activeSlideIndex].instance.update();
        }
      }, 100);

      popup.classList.add('is-open');
    });

    function updateCounter() {
      if (counterEl) {
        const currentSlide = popupSwiper.activeIndex + 1;
        const totalSlides = images.length;
        counterEl.textContent = `${currentSlide}/${totalSlides}`;
      }
    }
  }

  document.querySelector('.--mobile-full-screen-trigger')?.addEventListener('click', () => {
    const activeSlide = document.querySelector('.pdp-media-sm .swiper .swiper-slide-active');
    const activeSlideIndex = activeSlide ? Array.from(activeSlide.parentNode.children).indexOf(activeSlide) : 0;
    createZoomistPopup(activeSlideIndex);
  });

  document.querySelector('.zoomist-close')?.addEventListener('click', () => {
    const popup = document.getElementById('zoomist-popup');
    popup.classList.remove('is-open');
    popup.addEventListener('transitionend', function handler() {
      if (popupSwiper) {
        popupSwiper.destroy(true, true);
        popupSwiper = null;
      }
      zoomistInstances.forEach(({ instance }) => instance.destroy());
      zoomistInstances = [];
      popup.removeEventListener('transitionend', handler);
    }, { once: true });
  });
};

window.mblLightBoxFunc();

window.copyCode = async function (btn) {
  if (!btn) return;

  const root = btn.closest('.pdp-discount');
  const code = root?.dataset?.discount || '';
  const status = root.querySelector('.pdp-discount__status');

  try {
    await navigator.clipboard.writeText(code);
    status.classList.remove('hidden');
    try { localStorage.setItem('last_discount_code', code); } catch {}
  } catch (err) {
    status.textContent = 'Copy blocked';
    console.error(err);
  }

  clearTimeout(btn._t);
};


window.fastbundleatc = function (selector) {
  const fastBundleWidget = document.querySelector(selector);
  if (!fastBundleWidget) return;
  if (fastBundleWidget.children.length > 0) {
    window.runOnce?.();
    return;
  }
  const observer = new MutationObserver(() => {
    if (fastBundleWidget.children.length > 0) {
      window.runOnce?.();
    }
  });

  observer.observe(fastBundleWidget, { childList: true });
}

window.runOnce = function () {
  const atcButtons = document.querySelectorAll('.pinfo-form-wrapper');
  atcButtons.forEach(btn => {
    btn.classList.add('hidden');
  });
};

document.addEventListener('DOMContentLoaded', function() {
  window.fastbundleatc('.rbrFastBundlePage');
  window.functionsWithRetry('handleWair');
});